// Kartu Stok (Mutasi Stok Bahan Baku) — Phase 1D-δ-2 (read-only list).
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat
// halaman, saringan bawaan = 30 hari terakhir (periode terbaru; chip bisa dihapus).
//
// ⛔ DULU layar ini memindai SELURUH master bahan ke browser (`ambilSemua(listMaterials)`) hanya
//    untuk (a) dropdown saringan dan (b) menerjemahkan id bahan jadi kode di tiap baris. Di 5.000
//    bahan pindaian itu BERHENTI dengan galat dan kartu stok tidak bisa dibuka. Kini kode/nama
//    bahan + gudang ikut di baris dari server, dan saringan bahan memakai `PemilihCari`.
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listStockLedger, apiErrorMessage } from "../../lib/inventoryApi";
import { api } from "../../lib/api";
import { formatDateTime, isoPlusDays } from "../../lib/format";
import { movementTypeClass } from "../../components/etams/StatusBadge";
import Hint from "../../components/etams/Hint";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, ambilBahan, labelBahan } from "../../lib/sumberPilihan";
import { useAuth } from "../../contexts/AuthContext";

const MOVEMENT_TYPES = [
  "PURCHASE_IN", "OPENING_STOCK", "ADJUSTMENT_IN", "ADJUSTMENT_OUT",
  "MATERIAL_ISSUE", "RETURN_IN", "OPNAME",
];

// Kode tipe mutasi → kata yang dibaca staf gudang (nilai filter tetap kode).
const LABEL_TIPE = {
  PURCHASE_IN: "Pembelian masuk", OPENING_STOCK: "Stok awal", ADJUSTMENT_IN: "Adjustment masuk",
  ADJUSTMENT_OUT: "Adjustment keluar", MATERIAL_ISSUE: "Pengeluaran bahan", RETURN_IN: "Retur masuk",
  OPNAME: "Opname",
};
// Kode lain (belum ada di peta) dibaca sebagai kata biasa, bukan KODE_MENTAH.
const kataKode = (k) => (k ? k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, " ") : "—");
const labelTipe = (t) => LABEL_TIPE[t] || kataKode(t);

// KONTRAS-01 Batch 9 — tiga warna keras di sini pindah ke token lewat
// `movementTypeClass()`. Tiga makna tetap tiga tone: NOL informasi hilang.
// Percabangannya TIDAK disederhanakan — daftar kondisinya beda dari
// `FGMovement.jsx` (tabel yang berbeda, kosakata movement_type berbeda).
const TYPE_BADGE = (t) => {
  if (t?.endsWith("IN") || t === "PURCHASE_IN" || t === "OPENING_STOCK")
    return movementTypeClass("IN");
  if (t === "MATERIAL_ISSUE" || t?.endsWith("OUT"))
    return movementTypeClass("OUT");
  return movementTypeClass("OTHER");
};

//: Cermin `URUT_SAH_LEDGER` di backend/services/inventory_service.py.
const URUT_SAH = ["created_at", "qty_change", "movement_type"];

// Riwayat mencakup bahan yang sudah dinonaktifkan juga.
const cariBahanSemua = (kata) => cariBahan(kata, { status_aktif: "SEMUA" });

const KOLOM_CSV = [
  { kunci: "created_at", judul: "Tanggal", ambil: (l) => formatDateTime(l.created_at) },
  { kunci: "bahan", judul: "Bahan", ambil: (l) => labelBahan(l.material) },
  { kunci: "gudang", judul: "Gudang", ambil: (l) => l.store?.name || "" },
  { kunci: "movement_type", judul: "Tipe", ambil: (l) => labelTipe(l.movement_type) },
  { kunci: "qty_change", judul: "Perubahan" },
  { kunci: "qty_before", judul: "Sebelum" },
  { kunci: "qty_after", judul: "Sesudah" },
  { kunci: "reference_type", judul: "Rujukan", ambil: (l) => (l.reference_type ? kataKode(l.reference_type) : "") },
  { kunci: "notes", judul: "Catatan" },
];

export default function StockLedger() {
  const { roleCode, profile } = useAuth();
  // Saringan bawaan dari hak: Admin Gudang melihat gudangnya sendiri dulu (chip bisa dihapus).
  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [bahanTerpilih, setBahanTerpilih] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat.")));
  }, []);

  const namaGudang = (id) => stores.find(s => s.id === id)?.name || "…";
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      bahan: { label: "Bahan", param: "material_id",
               tampil: (v) => (bahanTerpilih?.id === v ? labelBahan(bahanTerpilih) : "…") },
      gudang: { label: "Gudang", param: "store_id", bawaan: gudangSaya, tampil: namaGudang },
      tipe: { label: "Tipe", param: "movement_type", tampil: labelTipe },
      // Periode terbaru — bawaan 30 hari terakhir; hapus chip-nya untuk melihat seluruh riwayat.
      dari: { label: "Dari", param: "date_from", bawaan: isoPlusDays(-30) },
      sampai: { label: "Sampai", param: "date_to" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) =>
    (await listStockLedger(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listStockLedger(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat mutasi stok.")); }
    finally { setLoading(false); }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const idBahan = daftar.nilaiSaringan.bahan;
  // Alamat halaman menyimpan id bahan saja — namanya dibaca sekali untuk chip + kotak pilih.
  useEffect(() => {
    if (!idBahan || bahanTerpilih?.id === idBahan) return;
    ambilBahan(idBahan).then(setBahanTerpilih)
      .catch((e) => toast.error(apiErrorMessage(e, "Bahan yang disaring gagal dibaca.")));
  }, [idBahan, bahanTerpilih?.id]);
  const pilihBahan = (m) => { setBahanTerpilih(m); daftar.setSaringan("bahan", m?.id || ""); };

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <History className="h-6 w-6" /> Mutasi Stok Bahan Baku
            <Hint>Catatan stok bahan baku — hanya bertambah, tidak pernah dihapus. Semua perubahan tercatat di sini.</Hint>
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
        </Button>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari kode / nama bahan / catatan" testId="ledger-palang"
                    ekspor={{ nama: "kartu_stok_bahan", kolom: KOLOM_CSV, ambilHalaman }}>
        <PemilihCari value={idBahan ? (bahanTerpilih?.id === idBahan ? bahanTerpilih : idBahan) : null}
                     onChange={pilihBahan} cari={cariBahanSemua} ambilSatu={ambilBahan}
                     label={labelBahan} placeholder="Semua bahan" bolehKosong className="w-56"
                     testId="ledger-material-filter" />
        <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="ledger-store-filter" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={daftar.nilaiSaringan.tipe} onChange={(e) => daftar.setSaringan("tipe", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="ledger-type-filter" aria-label="Saring tipe">
          <option value="">Semua Tipe</option>
          {MOVEMENT_TYPES.map(t => <option key={t} value={t}>{labelTipe(t)}</option>)}
        </select>
        <Input type="date" value={daftar.nilaiSaringan.dari} className="w-40" aria-label="Dari tanggal"
               onChange={(e) => daftar.setSaringan("dari", e.target.value)} data-testid="ledger-from" />
        <Input type="date" value={daftar.nilaiSaringan.sampai} className="w-40" aria-label="Sampai tanggal"
               onChange={(e) => daftar.setSaringan("sampai", e.target.value)} data-testid="ledger-to" />
      </PalangDaftar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                <th className="px-4 py-3 font-medium">Bahan</th>
                <th className="px-4 py-3 font-medium">Gudang</th>
                <ThUrut daftar={daftar} kunci="movement_type" className="px-4 py-3 font-medium">Tipe</ThUrut>
                <ThUrut daftar={daftar} kunci="qty_change" className="px-4 py-3 font-medium" align="right">Perubahan</ThUrut>
                <th className="px-4 py-3 font-medium text-right">Sebelum</th>
                <th className="px-4 py-3 font-medium text-right">Sesudah</th>
                <th className="px-4 py-3 font-medium">Rujukan</th>
                <th className="px-4 py-3 font-medium">Catatan</th>
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="9" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="9" className="p-12 text-center text-muted-foreground text-sm" data-testid="ledger-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length
                    ? "Tidak ada mutasi yang cocok dengan pencarian atau saringan ini."
                    : "Belum ada mutasi stok."}
                </td></tr>
              )}
              {daftar.tampil.map(l => (
                <tr key={l.id} className="border-b hover:bg-muted/50">
                  <td className="px-4 py-3 whitespace-nowrap text-xs">{formatDateTime(l.created_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="font-mono">{l.material?.code || "—"}</span>
                    {l.material?.name && <span className="block text-muted-foreground">{l.material.name}</span>}
                  </td>
                  <td className="px-4 py-3">{l.store?.name || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge className={TYPE_BADGE(l.movement_type)}>{labelTipe(l.movement_type)}</Badge>
                  </td>
                  <td className={`px-4 py-3 text-right font-semibold ${Number(l.qty_change) < 0 ? "text-danger-soft-foreground" : "text-success-soft-foreground"}`}>
                    {Number(l.qty_change) > 0 ? "+" : ""}{l.qty_change}
                  </td>
                  <td className="px-4 py-3 text-right">{l.qty_before}</td>
                  <td className="px-4 py-3 text-right font-semibold">{l.qty_after}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {l.reference_type ? kataKode(l.reference_type) : "-"}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">{l.notes || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="ledger-paginasi" />
      </Card>
    </div>
  );
}
